import { SagaIterator } from 'redux-saga';
import { put, select } from 'redux-saga/effects';
import { StorageKey } from '../../../constants/storage';
import { ClientError, DataResponse, FailureOperation, isFailureResponse } from '../../../models/common';
import { isDate } from '../../../utilities/date';
import { isObject } from '../../../utilities/object';
import {
    syncDisableRemoteDesktopStatus,
    syncDisableRemoteDesktopStatusError,
    syncDisableRemoteDesktopStatusFailed,
    syncDisableRemoteDesktopStatusSuccess,
} from '../../actions/application/application-action-creators';
import {
    DisableRemoteDesktopStatusPayload,
    SyncDisableRemoteDesktopStatusAction,
} from '../../actions/application/application-actions';
import { getStorageValue } from '../../actions/storage/storage-action-creators';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeEvery } from '../../effects/take';
import { getStorageType } from '../../selector/storage-selectors';

const isDisableRemoteDesktopStatus = (
    obj: DisableRemoteDesktopStatusPayload
): obj is DisableRemoteDesktopStatusPayload => {
    return (
        isObject(obj) &&
        isDate(
            typeof obj.siteLoadSinceDisableRemoteDesktop === 'string'
                ? new Date(obj.siteLoadSinceDisableRemoteDesktop)
                : obj.siteLoadSinceDisableRemoteDesktop
        )
    );
};

const convertStringToDisableRemoteDesktopStatus = (status: string): DisableRemoteDesktopStatusPayload => {
    const maybeDisableRemoteDesktopStatus = JSON.parse(status);

    if (!isDisableRemoteDesktopStatus(maybeDisableRemoteDesktopStatus)) {
        throw new ClientError('String could not be parsed as disable remote desktop settings.');
    }

    return maybeDisableRemoteDesktopStatus;
};

export function* syncDisableRemoteDesktopStatusSaga(action: SyncDisableRemoteDesktopStatusAction): SagaIterator {
    try {
        const storageType = yield select(getStorageType);
        const response: DataResponse<string> = yield putAndAwait(
            getStorageValue({
                getForSignedInUser: true,
                key: StorageKey.DisableRemoteDesktopStatus,
                storageType,
            })
        );

        if (isFailureResponse(response)) {
            yield put(syncDisableRemoteDesktopStatusFailed({ failure: response }));
            yield resolveAction(action, response);
            return;
        }

        const { data } = response;
        const disableRemoteDesktopStatus = !!data ? convertStringToDisableRemoteDesktopStatus(data) : {};

        yield put(syncDisableRemoteDesktopStatusSuccess(disableRemoteDesktopStatus));
        yield resolveAction(action, { data: disableRemoteDesktopStatus, succeeded: true });
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.SyncDisableRemoteDesktopStatus);
        yield put(syncDisableRemoteDesktopStatusError({ error }));
        yield rejectAction(action, error);
    }
}

export function* syncDisableRemoteDesktopStatusListenerSaga(): SagaIterator {
    yield takeEvery(syncDisableRemoteDesktopStatus, syncDisableRemoteDesktopStatusSaga);
}
